import { defineStore } from 'pinia';
import { computed, watch } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import FetchDataClient from '@/api/FetchDataClient';
import { useGameProgressStore } from './gameProgressStore';
import { useGameStore } from './gameStore';
import { User, AuthResponse, AuthState } from '@/types/user';
import { format, isToday, parseISO } from 'date-fns';
import { createUserStorage } from '@/utils/userStorage';

// Initialize with stored token if available
let initialToken = null;
try {
  const storedAuth = localStorage.getItem('user.auth');
  if (storedAuth) {
    const auth = JSON.parse(storedAuth);
    initialToken = auth.token;
  }
} catch (e) {
  console.warn('Failed to load initial token in userStore:', e);
}

const api = new FetchDataClient(initialToken);

export const useUserStore = defineStore('user', () => {
  // Persistent state using useLocalStorage
  const auth = useLocalStorage<AuthState>('user.auth', {
    token: null,
    refresh_token: null,
    user_id: null,
  });

  // Use custom storage for user data to avoid serialization issues
  const user = createUserStorage();

  const loggedIn = useLocalStorage('user.loggedIn', false);

  // Track the date when games were played for daily reset
  const lastPlayDate = useLocalStorage<string | null>(
    'user.lastPlayDate',
    null
  );
  const gamesPlayedToday = useLocalStorage<number>('user.gamesPlayedToday', 0);

  const sanitizeUser = (userData: Partial<User>): User => {
    // Handle features that come as array from API
    let features = {};

    if (userData.features) {
      if (Array.isArray(userData.features)) {
        // Convert array format [{key: "junior_access", value: true}] to object {junior_access: true}
        features = userData.features.reduce((acc, feature) => {
          if (feature.key && feature.value !== undefined) {
            acc[feature.key] = feature.value;
          }
          return acc;
        }, {});
      } else {
        // Already in object format
        features = userData.features;
      }
    }

    const sanitized = {
      id: userData.id || '',
      first_name: userData.first_name || '',
      last_name: userData.last_name || '',
      name: userData.name || '',
      email: userData.email || '',
      gender: userData.gender || '',
      active: userData.active || 0,
      tickets: userData.tickets ?? 0, // Use ?? to preserve 0 values
      completed_plays: userData.completed_plays || 0,
      loggedIn: true,
      features,
    };

    // Debug: Check if the object can be serialized
    try {
      const testSerialize = JSON.stringify(sanitized);
      if (testSerialize === '[object Object]' || !testSerialize) {
        console.error(
          '⚠️ Sanitized user object cannot be properly serialized!'
        );
        console.error('Sanitized object:', sanitized);
      }
    } catch (e) {
      console.error('⚠️ Failed to serialize sanitized user:', e);
      console.error('Sanitized object:', sanitized);
    }

    return sanitized;
  };

  async function login(
    email: string,
    password: string
  ): Promise<boolean | { success: false; message: string }> {
    try {
      console.log('Attempting to login with email:', email);

      // Make login request without token (since we're authenticating)
      const response = await api.post<AuthResponse>('/login', {
        identifier: email,
        password,
      });

      if (response?.user && response?.token) {
        console.log('Login successful, received token');

        // Important: First set the token in the API client
        api.setAxiosHeaders(response.token);

        // Then update local state and other stores
        await handleSuccessfulAuthentication(response);
        return true;
      }

      console.error('Invalid login response:', response);
      throw new Error('Invalid login response - missing user or token');
    } catch (error: unknown) {
      console.error('Login failed:', error);

      // Extract error message from the error
      let errorMessage = 'Invalid email or password. Please try again.';

      if (error instanceof Error && error.message) {
        // Parse the error message if it contains JSON
        const messageMatch = error.message.match(/\{"message":\[(.*?)\]\}/);
        if (messageMatch) {
          try {
            const parsed = JSON.parse(messageMatch[0]);
            if (
              parsed.message &&
              Array.isArray(parsed.message) &&
              parsed.message.length > 0
            ) {
              errorMessage = parsed.message[0];
            }
          } catch (e) {
            // If parsing fails, use the default message
          }
        }
      }

      return { success: false, message: errorMessage };
    }
  }

  async function logout(): Promise<void> {
    const gameProgressStore = useGameProgressStore();
    const gameStore = useGameStore();

    (Object.keys(auth.value) as Array<keyof AuthState>).forEach((key) => {
      auth.value[key] = null;
    });
    loggedIn.value = false;
    user.value = null;

    // Reset progress including tickets
    gameProgressStore.resetProgress();
    gameStore.updateGames([]);
    gameStore.setInMission(false);
    gameStore.setMissionEnd(null);

    // Note: We intentionally do not clear completedDemos when logging out
    // These are persistently stored per user's device

    api.setAxiosHeaders(null);
  }

  async function handleSuccessfulAuthentication(response: AuthResponse) {
    const gameProgressStore = useGameProgressStore();
    const gameStore = useGameStore();
    console.log('📥 Authentication response:', response);

    // Prioritize tickets from response root, then user object
    const ticketsFromResponse = response.tickets ?? response.user.tickets ?? 0;

    console.log('🎫 Tickets from auth response:', {
      responseTickets: response.tickets,
      userTickets: response.user.tickets,
      finalTickets: ticketsFromResponse,
    });

    // Merge user features with top-level features from response
    // Also ensure tickets from response are used (not from user object)
    const userWithMergedFeatures = {
      ...response.user,
      tickets: ticketsFromResponse, // Use the resolved tickets value
      features: {
        ...response.user.features,
        ...(response.features || {}), // Merge any top-level features
      },
    };

    const sanitizedUser = sanitizeUser(userWithMergedFeatures);
    console.log('✅ Sanitized user with features and tickets:', sanitizedUser);

    // Update auth and user state
    auth.value = {
      token: response.token,
      refresh_token: response.refresh_token,
      user_id: response.user.id,
    };
    loggedIn.value = true;
    user.value = sanitizedUser;

    // Update game progress - use setTickets to SET the value, not ADD to it
    console.log(
      '🎫 Setting tickets in gameProgressStore:',
      ticketsFromResponse
    );
    gameProgressStore.setTickets(ticketsFromResponse);
    gameProgressStore.completed_plays = response.completed_plays;
    gameProgressStore.gamesPlayed = response.completed_plays;

    // Set the games played today counter
    setGamesPlayedToday(response.completed_plays);

    // Update game state
    gameStore.updateGames(response.games.games);
    gameStore.setInMission(response.games.in_mission);
    gameStore.setMissionEnd(
      typeof response.games.mission_end === 'number'
        ? response.games.mission_end
        : null
    );

    // Important: This sets the token for future API requests
    console.log(
      'Setting API token after authentication:',
      response.token ? 'Token received' : 'No token received'
    );
    api.setAxiosHeaders(response.token);

    // Verify token was set properly
    console.log(
      'Token verification after setting:',
      api.getAuthToken() ? 'Token set successfully' : 'Token not set properly'
    );
  }

  async function checkAuth(): Promise<boolean> {
    if (auth.value.token) {
      api.setAxiosHeaders(auth.value.token);

      try {
        // Pass token explicitly to ensure it's used
        const response = await api.get<{
          user: User;
          completed_plays?: number;
          tickets?: number;
          games?: AuthResponse['games'];
        }>('/user', auth.value.token);

        if (response?.user) {
          // Prioritize tickets from response root, then user object
          const ticketsFromResponse =
            response.tickets ?? response.user.tickets ?? 0;

          console.log('📥 checkAuth received data:', {
            responseTickets: response.tickets,
            userTickets: response.user.tickets,
            finalTickets: ticketsFromResponse,
          });

          // Update user object with the correct tickets value
          const userWithCorrectTickets = {
            ...response.user,
            tickets: ticketsFromResponse,
          };

          user.value = sanitizeUser(userWithCorrectTickets);

          // Sync tickets with gameProgressStore after restoring session
          const gameProgressStore = useGameProgressStore();
          console.log('🎫 Syncing tickets to stores:', ticketsFromResponse);
          gameProgressStore.setTickets(ticketsFromResponse);
          // Sync mission + games payload to overwrite stale localStorage state on reload
          // Mission reward config (if provided by /user)
          const gameStore = useGameStore();
          if (Array.isArray(response.games?.games)) {
            gameStore.updateGames(response.games.games);
          }
          if (typeof response.games?.in_mission === 'boolean') {
            gameStore.setInMission(response.games.in_mission);
          } else if (response.games) {
            gameStore.setInMission(false);
          }
          if (typeof response.games?.mission_end === 'number') {
            gameStore.setMissionEnd(response.games.mission_end);
          } else if (response.games) {
            gameStore.setMissionEnd(null);
          }

          // Set games played today if available from API
          if (response.completed_plays !== undefined) {
            setGamesPlayedToday(response.completed_plays);
          } else if (user.value.completed_plays !== undefined) {
            setGamesPlayedToday(user.value.completed_plays);
          }

          return true;
        }
        throw new Error('Invalid user data');
      } catch (error) {
        console.error('Failed to restore session:', error);
        await logout();
        return false;
      }
    } else {
      console.warn('CheckAuth: No token found in auth store');
    }

    return false;
  }

  function increaseTickets(ticketsToAdd: number): void {
    if (user.value) {
      user.value.tickets += ticketsToAdd;
      // Sync with gameProgressStore to maintain consistency
      const gameProgressStore = useGameProgressStore();
      gameProgressStore.setTickets(user.value.tickets);
    }
  }

  // decreaseTickets method that passes a new number of tickets to subtract from the existing user.tickets value
  function decreaseTickets(ticketsToSubtract: number): void {
    if (user.value && user.value.tickets >= ticketsToSubtract) {
      user.value.tickets -= ticketsToSubtract;
      // Sync with gameProgressStore to maintain consistency
      const gameProgressStore = useGameProgressStore();
      gameProgressStore.setTickets(user.value.tickets);
    }
  }

  const getToken = computed(() => auth.value.token);
  const hasJuniorAccess = computed(
    () => user.value?.features?.junior_access || false
  );
  const getUserFeatures = computed(() => user.value?.features || {});

  // Debug function to verify ticket consistency
  function verifyTicketConsistency(): void {
    const gameProgressStore = useGameProgressStore();
    const userTickets = user.value?.tickets ?? 0;
    const progressTickets = gameProgressStore.tickets;

    if (userTickets !== progressTickets) {
      console.warn('⚠️ Ticket mismatch detected!', {
        userStore: userTickets,
        gameProgressStore: progressTickets,
        difference: userTickets - progressTickets,
      });

      // Auto-sync to userStore value as source of truth
      gameProgressStore.setTickets(userTickets);
      console.log('✅ Auto-synced tickets to:', userTickets);
    }
  }

  // Check if we need to reset the daily games counter
  function checkAndResetDailyGames(): void {
    const today = format(new Date(), 'yyyy-MM-dd');
    const storedDate = lastPlayDate.value;

    console.log('📅 Checking date: stored=' + storedDate + ', today=' + today);

    if (storedDate && storedDate !== today) {
      // It's a new day, reset the counter
      console.log('🎅 New day detected, resetting games played counter');
      console.log('  Previous date: ' + storedDate);
      console.log('  Current date: ' + today);
      console.log('  Previous count: ' + gamesPlayedToday.value);

      gamesPlayedToday.value = 0;
      if (
        user.value &&
        typeof user.value === 'object' &&
        'completed_plays' in user.value
      ) {
        user.value.completed_plays = 0;
      }

      // Also reset in gameProgressStore
      const gameProgressStore = useGameProgressStore();
      gameProgressStore.setCompletedPlays(0);
    }

    // Always update the date to today
    lastPlayDate.value = today;
  }

  // Increment games played today (called when a game completes)
  function incrementGamesPlayedToday(): void {
    checkAndResetDailyGames();

    // Get the current value to ensure we're incrementing from the right base
    const currentCount = gamesPlayedToday.value || 0;
    const newCount = currentCount + 1;

    // Update reactive ref (which updates localStorage automatically via useLocalStorage)
    gamesPlayedToday.value = newCount;

    // Update user object
    if (user.value) {
      user.value.completed_plays = newCount;
    }

    // Also update gameProgressStore for consistency
    const gameProgressStore = useGameProgressStore();
    gameProgressStore.incrementCompletedPlays();

    console.log(
      '🎮 Game completed! Games played today: ' +
        currentCount +
        ' → ' +
        newCount
    );
  }

  // Set games played today from API response
  function setGamesPlayedToday(count: number): void {
    // First check if we need to reset (in case it's a new day)
    checkAndResetDailyGames();

    // Only update if the value actually changed to avoid unnecessary reactivity
    if (gamesPlayedToday.value !== count) {
      const oldValue = gamesPlayedToday.value;
      gamesPlayedToday.value = count;

      if (user.value) {
        user.value.completed_plays = count;
      }

      // Also update gameProgressStore for consistency
      const gameProgressStore = useGameProgressStore();
      gameProgressStore.setCompletedPlays(count);

      console.log('📊 Games played today updated: ' + oldValue + ' → ' + count);
    }
  }

  // Watch for day changes while the app is running
  if (typeof window !== 'undefined') {
    setInterval(() => {
      checkAndResetDailyGames();
    }, 60000); // Check every minute
  }

  return {
    // State
    auth,
    user,
    loggedIn,
    gamesPlayedToday,
    lastPlayDate,

    // Getters
    isLoggedIn: computed(() => !!auth.value.token),
    hasJuniorAccess,
    getUserFeatures,

    // Actions
    login,
    logout,
    handleSuccessfulAuthentication,
    checkAuth,
    increaseTickets,
    decreaseTickets,
    verifyTicketConsistency,
    getToken,
    incrementGamesPlayedToday,
    setGamesPlayedToday,
    checkAndResetDailyGames,
  };
});
